/**
 * Phase-4 editor forms: activities (with the verify-a-pin flow), lodgings,
 * booking-checklist entries, and trip settings. Same shape as editor.ts —
 * plain forms over typed ops through postEditorOps; nothing mutates store.def.
 *
 * Pins: the form only ever proposes coordinates that came from the server's
 * own geocoder (/geocode candidates). The def route re-verifies every
 * coordinate against the water rule regardless, so even a hand-crafted POST
 * can't put a restaurant in the Adriatic.
 */
import type { DefOp } from "@trip/shared/defops";
import { esc } from "@trip/shared/engine/time";
import type { Act } from "@trip/shared/types";
import { store } from "../store.js";
import { banner } from "./banner.js";
import { postEditorOps } from "./editor.js";

const modal = () => document.getElementById("modal")!;
const openBg = () => document.getElementById("modalbg")!.classList.add("open");

const fld = (id: string, label: string, value: string, extra = "") => `
  <label class="ed-fld"><span>${label}</span>
    <input id="${id}" value="${esc(value)}" ${extra}></label>`;

const auth = (extra: Record<string, string> = {}) => ({
  ...extra,
  ...(store.key ? { "x-trip-key": store.key } : {}),
});

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "activity";

const freshId = (base: string, taken: (id: string) => boolean) => {
  let id = base;
  for (let n = 2; taken(id); n++) id = `${base}-${n}`.slice(0, 47);
  return id;
};

/* ---------------- the pin picker, shared by activity + stay forms ---------------- */

interface PinPick {
  geo: [number, number] | null;
  cleared: boolean;
}

/** Renders the address-search block; returns a getter for what the user chose. */
function wirePinPicker(container: HTMLElement, existing: [number, number] | null): () => PinPick {
  const state: PinPick = { geo: null, cleared: false };
  container.innerHTML = `
    <div class="ed-fld"><span>Map pin</span>
      <div class="geo-row">
        <input id="geo-q" placeholder="search an address or place name" ${existing ? `value=""` : ""}>
        <button type="button" class="btn-det" id="geo-find">Find</button>
      </div>
      <div id="geo-status" class="dir-note">${
        existing ? `Currently pinned at ${existing[0].toFixed(4)}, ${existing[1].toFixed(4)}. <button type="button" class="btn-det btn-sm" id="geo-clear">Remove pin</button>` : "No pin yet — unpinned stops use a flat 10 min travel estimate."
      }</div>
      <div id="geo-cands"></div>
    </div>`;

  const status = () => container.querySelector<HTMLElement>("#geo-status")!;
  container.querySelector<HTMLButtonElement>("#geo-clear")?.addEventListener("click", () => {
    state.cleared = true;
    state.geo = null;
    status().textContent = "Pin will be removed on save.";
  });

  container.querySelector<HTMLButtonElement>("#geo-find")!.addEventListener("click", async () => {
    const q = container.querySelector<HTMLInputElement>("#geo-q")!.value.trim();
    if (q.length < 3) return banner("Type an address or place to search for.", "warn");
    const cands = container.querySelector<HTMLElement>("#geo-cands")!;
    cands.innerHTML = `<div class="share-empty">Searching…</div>`;
    try {
      const res = await fetch(`/api/trip/${encodeURIComponent(store.tripId)}/geocode?q=${encodeURIComponent(q)}`, { headers: auth() });
      const data = (await res.json()) as { candidates?: { geo: [number, number]; label: string; verdict: string }[]; error?: string };
      if (!res.ok || !data.candidates) {
        cands.innerHTML = `<div class="share-result share-error" style="display:block">${esc(data.error ?? "Search failed.")}</div>`;
        return;
      }
      if (!data.candidates.length) {
        cands.innerHTML = `<div class="share-empty">Nothing found — try adding the city.</div>`;
        return;
      }
      cands.innerHTML = data.candidates
        .map((cd, i) => {
          const water = cd.verdict === "water";
          return `<label class="geo-cand${water ? " geo-water" : ""}">
            <input type="radio" name="geo-pick" value="${i}" ${water ? "disabled" : ""}>
            <span>${esc(cd.label)}</span>${water ? `<span class="badge b-view">water — can't pin</span>` : ""}
          </label>`;
        })
        .join("");
      cands.querySelectorAll<HTMLInputElement>("input[name=geo-pick]").forEach((r) =>
        r.addEventListener("change", () => {
          const cd = data.candidates![Number(r.value)];
          state.geo = cd.geo;
          state.cleared = false;
          status().textContent = `Will pin at ${cd.geo[0].toFixed(4)}, ${cd.geo[1].toFixed(4)} — verified against the map on save.`;
        })
      );
    } catch {
      cands.innerHTML = `<div class="share-result share-error" style="display:block">Couldn't reach the server.</div>`;
    }
  });

  return () => state;
}

/* ---------------- activity form ---------------- */

export function openActForm(actId: string | null): void {
  const def = store.def;
  const a: Act | null = actId ? def.act[actId] ?? null : null;
  const existingPin = actId && def.geo[actId] ? ([def.geo[actId][0], def.geo[actId][1]] as [number, number]) : null;

  modal().style.maxWidth = "560px";
  modal().innerHTML = `
    <div class="m-head"><div class="m-cat">Edit trip</div><h3>${a ? `Edit ${esc(a.name)}` : "New activity"}</h3>
      <button class="m-close" data-action="close" aria-label="close">✕</button></div>
    <div class="m-body"><form id="ed-act-form" novalidate>
      ${fld("eda-name", "Name", a?.name ?? "", 'maxlength="120"')}
      <div class="share-fields">
        ${fld("eda-cat", "Category", a?.cat ?? "", 'maxlength="40" placeholder="e.g. Food & drink"')}
        ${fld("eda-durs", "Durations (hours)", (a?.durs ?? []).join(", "), 'placeholder="1.5, 2"')}
      </div>
      <div class="share-fields">
        ${fld("eda-price", "Price", a?.price ?? "", 'maxlength="120" placeholder="€€ — mains €15-25"')}
        ${fld("eda-hours", "Hours", a?.hours ?? "", 'maxlength="120" placeholder="12:00-23:00 daily"')}
      </div>
      ${fld("eda-addr", "Address (shown on the card)", a?.addr ?? "", 'maxlength="200"')}
      ${fld("eda-web", "Website", a?.web ?? "", 'maxlength="300" placeholder="https://…"')}
      <label class="ed-fld"><span>Notes</span><textarea id="eda-notes" maxlength="400" rows="2">${esc(a?.notes ?? "")}</textarea></label>
      <div class="share-fields">
        <label class="ed-fld"><span>Booking</span>
          <select id="eda-badge">
            <option value="" ${!a?.badge ? "selected" : ""}>— not recorded</option>
            <option value="book" ${a?.badge === "book" ? "selected" : ""}>⚑ Reservation required — adds it to the booking checklist once confirmed</option>
            <option value="free" ${a?.badge === "free" ? "selected" : ""}>Walk-up — no reservation needed</option>
          </select></label>
        ${fld("eda-fs", "Fixed start (optional)", a?.fs ?? "", 'type="time"')}
      </div>
      <div id="eda-pin"></div>
      <div class="share-copyrow"><button class="btn-cta" type="submit">${a ? "Save activity" : "Create activity"}</button></div>
      <div class="dir-note">Pins are verified against real map data on save — a point in open water is refused. Offer the activity in a day's dropdown from the 🗂 Activities panel.</div>
    </form></div>`;
  openBg();

  const pin = wirePinPicker(document.getElementById("eda-pin")!, existingPin);

  document.getElementById("ed-act-form")!.addEventListener("submit", async (e) => {
    e.preventDefault();
    const v = (id: string) => (document.getElementById(id) as HTMLInputElement).value.trim();
    const name = v("eda-name");
    if (!name) return banner("The activity needs a name.", "warn");
    const durs = v("eda-durs")
      .split(/[,\s]+/)
      .map(Number)
      .filter((d) => Number.isFinite(d) && d > 0 && d <= 12)
      .slice(0, 3);
    if (!durs.length) return banner("Give at least one duration in hours, e.g. 1.5", "warn");

    const act: Act = { name: name.slice(0, 120), durs };
    const set = (k: keyof Act, val: string) => { if (val) (act as unknown as Record<string, unknown>)[k] = val; };
    set("cat", v("eda-cat").slice(0, 40));
    set("price", v("eda-price").slice(0, 120));
    set("hours", v("eda-hours").slice(0, 120));
    set("addr", v("eda-addr").slice(0, 200));
    set("notes", (document.getElementById("eda-notes") as HTMLTextAreaElement).value.trim().slice(0, 400));
    set("fs", v("eda-fs"));
    const web = v("eda-web");
    if (web) {
      if (!/^https?:\/\//i.test(web)) return banner("Website links must start with http(s)://", "warn");
      act.web = web.slice(0, 300);
    }
    const badge = (document.getElementById("eda-badge") as HTMLSelectElement).value;
    if (badge === "book" || badge === "free") act.badge = badge;

    const id = actId ?? freshId(slugify(name), (x) => Boolean(store.def.act[x]));
    const origin = actId ? store.def.actOrigin?.[actId] ?? "app" : "app";
    const p = pin();
    const ops: DefOp[] = [];
    // a brand-new pin in a region-less trip needs a region to hang on
    if (p.geo && !Object.keys(store.def.rc).length) {
      ops.push({ op: "region.upsert", code: "main", color: "#2a7f62", name: "Map" });
    }
    ops.push({ op: "act.upsert", id, act, origin: origin === "extracted" ? "app" : origin, ...(p.geo ? { geo: p.geo } : {}) });
    if (p.cleared && existingPin) ops.push({ op: "act.setGeo", id, geo: null });

    if (await postEditorOps(ops)) banner(actId ? "Activity saved." : `Added "${act.name}" — offer it in a day from the Activities panel.`, "ok");
  });
}

/* ---------------- lodgings ---------------- */

export function openLodgings(): void {
  const def = store.def;
  const ids = def.stayIds ?? [];
  const curated = def.stays.length - ids.length;

  const stayName = (id: string) => {
    const at = ids.indexOf(id);
    return at !== -1 ? def.stays[curated + at]?.[1] ?? id : id;
  };
  const options = (sel: string) =>
    Object.keys(def.hotelPts)
      .map((id) => `<option value="${esc(id)}" ${id === sel ? "selected" : ""}>${esc(stayName(id))}</option>`)
      .join("");

  modal().style.maxWidth = "620px";
  modal().innerHTML = `
    <div class="m-head"><div class="m-cat">Edit trip</div><h3>🏨 Lodgings</h3>
      <button class="m-close" data-action="close" aria-label="close">✕</button></div>
    <div class="m-body">
      <div class="act-cat">Places you're staying</div>
      <div class="stay-list">
        ${def.stays
          .map((s, i) => {
            const appIdx = i - curated;
            const id = appIdx >= 0 ? ids[appIdx] : null;
            return `<div class="stay-row">
              <span class="stay-pin">${s[0]}</span><span class="stay-label">${esc(s[1])}</span>
              ${id
                ? `<button class="btn-det btn-sm" data-action="ed-stay-edit" data-id="${esc(id)}">✎</button>
                   <button class="btn-det btn-sm" data-action="ed-stay-remove" data-id="${esc(id)}">✕</button>`
                : `<span class="dir-note" style="margin:0">from trip source</span>`}
            </div>`;
          })
          .join("") || `<div class="share-empty">No lodgings yet.</div>`}
      </div>
      <div class="share-copyrow"><button class="btn-cta" data-action="ed-stay-add">＋ Add a stay</button></div>

      <div class="act-cat" style="margin-top:14px">Each day's base</div>
      <div class="dir-note">Where the day starts and ends — travel times and the "back at base" target hang off this. Transition days can start at one stay and end at another.</div>
      <div class="home-grid">
        ${def.days
          .map((d) => {
            const home = def.dayHome[d.id];
            return `<div class="home-row"><span>${esc(d.dow)} ${esc(d.date)}</span>
              <select data-action="ed-home" data-day="${d.id}" data-side="0">
                <option value="">— no base —</option>${options(home?.[0] ?? "")}
              </select>
              <select data-action="ed-home" data-day="${d.id}" data-side="1" ${home ? "" : "disabled"}>
                <option value="">— same —</option>${options(home?.[1] && home[1] !== home[0] ? home[1] : "")}
              </select></div>`;
          })
          .join("")}
      </div>
    </div>`;
  openBg();
}

/** A start/end select changed — build the pair and save. */
export async function setDayHome(dayId: string): Promise<void> {
  const row = document.querySelectorAll<HTMLSelectElement>(`select[data-action="ed-home"][data-day="${dayId}"]`);
  const start = row[0]?.value ?? "";
  const end = row[1]?.value || start;
  const home: [string, string] | null = start ? [start, end] : null;
  if (await postEditorOps([{ op: "day.setHome", dayId, home }])) {
    banner("Base saved — travel times reflow.", "ok");
    openLodgings(); // re-render with fresh def after resync… next tick
  }
}

export function openStayForm(stayId: string | null): void {
  const def = store.def;
  const ids = def.stayIds ?? [];
  const curated = def.stays.length - ids.length;
  const at = stayId ? ids.indexOf(stayId) : -1;
  const label = at !== -1 ? def.stays[curated + at][1] : "";
  const existing = stayId && def.hotelPts[stayId] ? def.hotelPts[stayId] : null;

  modal().style.maxWidth = "520px";
  modal().innerHTML = `
    <div class="m-head"><div class="m-cat">Edit trip</div><h3>${stayId ? "Edit stay" : "Add a stay"}</h3>
      <button class="m-close" data-action="close" aria-label="close">✕</button></div>
    <div class="m-body"><form id="ed-stay-form" novalidate>
      ${fld("eds-label", "Name", label, 'maxlength="80" placeholder="e.g. Old Town apartment"')}
      <div id="eds-pin"></div>
      <div class="share-copyrow"><button class="btn-cta" type="submit">${stayId ? "Save stay" : "Add stay"}</button></div>
      <div class="dir-note">A stay needs a verified location — every travel time on its days is measured from here.</div>
    </form></div>`;
  openBg();

  const pin = wirePinPicker(document.getElementById("eds-pin")!, existing);

  document.getElementById("ed-stay-form")!.addEventListener("submit", async (e) => {
    e.preventDefault();
    const name = (document.getElementById("eds-label") as HTMLInputElement).value.trim();
    if (!name) return banner("The stay needs a name.", "warn");
    const p = pin();
    const geo = p.geo ?? existing;
    if (!geo) return banner("Search and pick the stay's location first.", "warn");
    const id = stayId ?? freshId(slugify(name), (x) => Boolean(store.def.hotelPts[x]));
    if (await postEditorOps([{ op: "stay.upsert", id, label: name, geo }])) {
      banner("Stay saved.", "ok");
      setTimeout(openLodgings, 400); // reopen the list once resync lands
    }
  });
}

export async function removeStay(id: string): Promise<void> {
  if (await postEditorOps([{ op: "stay.remove", id }])) {
    banner("Stay removed.", "ok");
    setTimeout(openLodgings, 400);
  }
}

/* ---------------- booking checklist entries ---------------- */

export function openBlForm(k: string | null): void {
  const def = store.def;
  const b = k ? def.bl.find((x) => x.k === k) ?? null : null;
  const actOpts = Object.entries(def.act)
    .filter(([, a]) => !a.nomodal)
    .map(([id, a]) => `<option value="${esc(id)}" ${b?.req?.includes(id) ? "selected" : ""}>${esc(a.name)}</option>`)
    .join("");

  modal().style.maxWidth = "560px";
  modal().innerHTML = `
    <div class="m-head"><div class="m-cat">Edit trip</div><h3>${b ? "Edit booking item" : "New booking item"}</h3>
      <button class="m-close" data-action="close" aria-label="close">✕</button></div>
    <div class="m-body"><form id="ed-bl-form" novalidate>
      ${fld("edb-what", "What to book", b?.what ?? "", 'maxlength="160"')}
      ${fld("edb-why", "Why it matters", b?.why ?? "", 'maxlength="300"')}
      <div class="share-fields">
        ${fld("edb-when", "When", b?.when ?? "", 'maxlength="80" placeholder="e.g. 2-3 weeks before"')}
        ${fld("edb-link", "Link label", b?.link ?? "", 'maxlength="80" placeholder="e.g. Official site"')}
      </div>
      ${fld("edb-url", "Booking URL", b?.url ?? "", 'maxlength="300" placeholder="https://…"')}
      <label class="ed-fld"><span>Applies when any of these is picked (none = always)</span>
        <select id="edb-req" multiple size="5">${actOpts}</select></label>
      <div class="share-copyrow">
        <button class="btn-cta" type="submit">${b ? "Save item" : "Add item"}</button>
        ${b ? `<button class="btn-det" type="button" id="edb-remove">✕ Remove</button>` : ""}
      </div>
    </form></div>`;
  openBg();

  document.getElementById("edb-remove")?.addEventListener("click", async () => {
    if (await postEditorOps([{ op: "bl.remove", k: k! }])) banner("Booking item removed.", "ok");
  });

  document.getElementById("ed-bl-form")!.addEventListener("submit", async (e) => {
    e.preventDefault();
    const v = (id: string) => (document.getElementById(id) as HTMLInputElement).value.trim();
    const what = v("edb-what");
    if (!what) return banner("Say what needs booking.", "warn");
    const url = v("edb-url");
    if (url && !/^https?:\/\//i.test(url)) return banner("Links must start with http(s)://", "warn");
    const req = [...(document.getElementById("edb-req") as HTMLSelectElement).selectedOptions].map((o) => o.value);
    const key = k ?? freshId(slugify(what), (x) => store.def.bl.some((e2) => e2.k === x));
    const ops: DefOp[] = [
      {
        op: "bl.upsert",
        k: key,
        bl: {
          what, why: v("edb-why"), when: v("edb-when"), url, link: v("edb-link") || "Book",
          cls: b?.cls ?? "",
          ...(req.length ? { req } : { always: 1 as const }),
        },
      },
    ];
    if (await postEditorOps(ops)) banner("Booking item saved.", "ok");
  });
}

/* ---------------- trip settings ---------------- */

const toHHMM = (min: number) => `${String(Math.floor(min / 60) % 24).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;

export function openTripSettings(): void {
  const def = store.def;
  modal().style.maxWidth = "480px";
  modal().innerHTML = `
    <div class="m-head"><div class="m-cat">Edit trip</div><h3>⚙ Trip settings</h3>
      <button class="m-close" data-action="close" aria-label="close">✕</button></div>
    <div class="m-body"><form id="ed-trip-form" novalidate>
      ${fld("edt-title", "Hero title", def.hero?.title ?? def.name, 'maxlength="120"')}
      ${fld("edt-sub", "Hero subtitle", def.hero?.sub ?? "", 'maxlength="160" placeholder="Travelers and dates"')}
      <div class="share-fields">
        ${fld("edt-dayend", "Default back-at-base", toHHMM(def.dayEnd), 'type="time"')}
        ${fld("edt-grace", "Late grace (min)", String(def.lateGrace ?? 45), 'type="number" min="0" max="600"')}
      </div>
      <div class="share-copyrow"><button class="btn-cta" type="submit">Save settings</button></div>
      <div class="dir-note">Per-day end targets (set on each day) override the default. The hero is the banner at the top.</div>
    </form></div>`;
  openBg();

  document.getElementById("ed-trip-form")!.addEventListener("submit", async (e) => {
    e.preventDefault();
    const v = (id: string) => (document.getElementById(id) as HTMLInputElement).value.trim();
    const [h, m] = v("edt-dayend").split(":").map(Number);
    const patch: { dayEnd?: number; lateGrace?: number; hero?: { title?: string; sub?: string } } = {};
    if (Number.isFinite(h)) patch.dayEnd = h * 60 + (m || 0);
    const grace = Number(v("edt-grace"));
    if (Number.isFinite(grace)) patch.lateGrace = grace;
    patch.hero = { title: v("edt-title"), sub: v("edt-sub") };
    if (await postEditorOps([{ op: "trip.set", patch }])) banner("Settings saved.", "ok");
  });
}
